<script setup lang="ts">
    import { computed, ref } from 'vue';
    import GroupLabel from '@/views/3d-planner/components/GroupLabel.vue';
    import AppModalActions from '@/components/modal/AppModalActions.vue';
    import AppModalContent from '@/components/modal/AppModalContent.vue';
    import AppModalTitle from '@/components/modal/AppModalTitle.vue';
    import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
    import { formatLength } from '@/util/format/formatLength';
    import { assert } from '@/util';
    import type { Adjustments } from '@/views/3d-planner/store/plannerState';
    import PlannerSlider from '@/views/3d-planner/components/PlannerSlider.vue';

    const store = usePlannerStore();

    const editDisabled = computed(() => store.targetAdjustments === null);
    const isDialogOpen = ref(false);
    const selectedAdjustments = ref<Adjustments>({ legLength: 0, offset: 0 });

    function openDialog() {
        assert(store.targetAdjustments);
        selectedAdjustments.value = { ...store.targetAdjustments };
        isDialogOpen.value = true;
    }

    const isDisabled = computed(() => {
        return (
            selectedAdjustments.value.legLength === store.targetAdjustments?.legLength &&
            selectedAdjustments.value.offset === store.targetAdjustments?.offset
        );
    });

    function onCancel() {
        isDialogOpen.value = false;
    }

    async function onSave() {
        isDialogOpen.value = false;
        await store.setTargetAdjustments(selectedAdjustments.value);
    }
</script>

<template>
    <div data-testid="optimisation-targets" class="optimisation-targets-container">
        <v-card class="optimisation-card" elevation="0">
            <v-card-text class="pt-5 pl-0 pb-5 pr-0">
                <div class="text-body-1 mb-3">Optimisation Targets</div>
                <div class="d-flex justify-space-between align-center mb-3 text-body-2">
                    <div>
                        <span class="text-secondary-text">Leg Length:</span>
                        <span class="font-weight-bold ml-1">{{
                            formatLength(store.targetAdjustments?.legLength)
                        }}</span>
                    </div>
                    <div class="pr-3">
                        <span class="text-secondary-text">Offset:</span>
                        <span class="font-weight-bold ml-1">{{
                            formatLength(store.targetAdjustments?.offset)
                        }}</span>
                    </div>
                </div>
                <div class="d-flex justify-end">
                    <app-button-secondary
                        class="edit-targets-btn"
                        :disabled="editDisabled"
                        @click="openDialog"
                        >Edit Targets
                    </app-button-secondary>
                </div>
            </v-card-text>
        </v-card>
        <app-modal v-model="isDialogOpen" width="600" persistent>
            <v-card>
                <app-modal-title @close="isDialogOpen = false"> Edit Targets</app-modal-title>

                <app-modal-content>
                    <div>
                        <group-label>Leg Length (mm)</group-label>
                        <planner-slider
                            v-model="selectedAdjustments.legLength"
                            :min="-20"
                            :max="20"
                        />
                    </div>
                    <div>
                        <group-label>Offset (mm)</group-label>
                        <planner-slider v-model="selectedAdjustments.offset" :min="-20" :max="20" />
                    </div>

                    <app-hint v-if="!isDisabled" type="warning" class="mt-5">
                        Warning: Adjusting Leg Length or Offset will re-rank the stem selection.
                        Re-ranking will consider the current cup size. Stem size and neck may be
                        adjusted
                    </app-hint>
                </app-modal-content>

                <app-modal-actions>
                    <app-button-secondary width="100" class="mr-5" @click="onCancel">
                        Cancel
                    </app-button-secondary>
                    <app-button-primary :disabled="isDisabled" @click="onSave"
                        >Save
                    </app-button-primary>
                </app-modal-actions>
            </v-card>
        </app-modal>
    </div>
</template>

<style scoped lang="scss">
    .optimisation-targets-container {
        margin-bottom: 16px;
    }

    .optimisation-card {
        border: 1px solid rgb(var(--v-theme-on-increment-grey));
        border-radius: 17px;
        background-color: transparent;
        padding-left: 10px;
        margin-left: -10px;
        padding-right: 10px;
        margin-right: -10px;
    }

    .edit-targets-btn {
        width: 110px !important;
    }
</style>
